"use client";

import {
  Activity,
  ChevronRight,
  CreditCard,
  Eye,
  FileText,
  HardDrive,
  Lock,
  Settings,
  Stethoscope,
  Video,
  Wifi,
  X,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { KioskDialog, KioskDialogContent } from "../elements/KioskDialog";
import {
  copyStateToClipboard,
  getSerializedStateSize,
} from "../machine/state-serializer";
import { ErrorsTab } from "./components/tabs/ErrorsTab";
import { InspectTab } from "./components/tabs/InspectTab";
import { JumpTab } from "./components/tabs/JumpTab";
import { NetworkTab } from "./components/tabs/NetworkTab";
import { PatchNotesTab } from "./components/tabs/PatchNotesTab";
import { SettingsTab } from "./components/tabs/SettingsTab";
import { StorageTab } from "./components/tabs/StorageTab";
import { StripeTab } from "./components/tabs/StripeTab";
import { TraceTab } from "./components/tabs/TraceTab";
import { VideoTab } from "./components/tabs/VideoTab";
import { HealthCheckTab } from "./components/tabs/health-check-tab";
import type { DevToolTab, KioskDevToolProps } from "./types";

// Password for DevTool access
const DEVTOOL_PASSWORD = "superscript4321!";
const AUTH_STORAGE_KEY = "kiosk-devtool-auth";

/**
 * Developer tool for inspecting and manipulating kiosk state
 *
 * Features:
 * - Inspect current machine state (value + context)
 * - Jump to any state in the flow
 * - Visualize state machine tree
 * - Copy state to clipboard
 *
 * Only renders in development mode.
 */
export function KioskDevTool({
  open,
  onOpenChange,
  state,
  flow,
  machine,
  onStateChange,
  onContextChange,
  inactivityTimeout,
  onInactivityTimeoutChange,
  stripeReaderId,
  onStripeReaderIdChange,
  configHealthCheck,
  onRerunHealthCheck,
}: KioskDevToolProps) {
  const [activeTab, setActiveTab] = useState<DevToolTab>("inspect");
  const [copied, setCopied] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [passwordInput, setPasswordInput] = useState("");
  const [showPasswordError, setShowPasswordError] = useState(false);

  // Check for QR mode
  const isQRMode = state.context.qrMode === true;

  // Check localStorage for existing auth on mount
  useEffect(() => {
    const storedAuth = localStorage.getItem(AUTH_STORAGE_KEY);
    if (storedAuth === "true") {
      setIsAuthenticated(true);
    }
  }, []);

  // Calculate size info
  const sizeInfo = useMemo(() => getSerializedStateSize(state), [state]);

  // Get current state name
  const currentStateName = (() => {
    if (typeof state.value === "string") {
      return state.value;
    }

    if (typeof state.value === "object" && state.value !== null) {
      // Nested state: { posPayment: "creatingPaymentIntent" }
      const [parentName, childName] = Object.entries(state.value)[0] ?? [];
      return parentName; // Use parent name we already extracted
    }

    return String(state.value);
  })();

  // Determine button color based on URL length
  let bgColor = "bg-gray-900 hover:bg-gray-800";
  if (!sizeInfo.isWithinServerLimit) {
    bgColor = "bg-red-600 hover:bg-red-700";
  } else if (!sizeInfo.isWithinRecommendedLimit) {
    bgColor = "bg-orange-600 hover:bg-orange-700";
  }

  const handleCopy = async () => {
    try {
      // Extract current stateId from URL to reuse it
      const url = new URL(window.location.href);
      const currentStateId = url.searchParams.get("stateId");

      await copyStateToClipboard(state, currentStateId || undefined);
      setCopied(true);
      console.log("[KioskDevTool] ✅ State copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error("[KioskDevTool] ❌ Failed to copy state:", error);
    }
  };

  const handleJumpToState = (
    stateName: string,
    arc?: { arcId: string; stateName: string | Record<string, unknown> },
  ) => {
    onStateChange(stateName, arc);
    onOpenChange(false);
  };

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (passwordInput === DEVTOOL_PASSWORD) {
      setIsAuthenticated(true);
      localStorage.setItem(AUTH_STORAGE_KEY, "true");
      setShowPasswordError(false);
      setPasswordInput("");
    } else {
      setShowPasswordError(true);
      setTimeout(() => setShowPasswordError(false), 3000);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem(AUTH_STORAGE_KEY);
    onOpenChange(false);
  };

  // Keyboard shortcut to open devtool (Ctrl+Shift+D or Cmd+Shift+D)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isQRMode) return;
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === "D") {
        e.preventDefault();
        onOpenChange(true);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isQRMode, onOpenChange]);

  return (
    <div data-devtool>
      {/* DevTool Dialog */}
      <KioskDialog open={open} onOpenChange={onOpenChange}>
        <KioskDialogContent
          className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col"
          data-devtool
        >
          {!isAuthenticated ? (
            /* Password Protection Screen */
            <div className="flex-1 flex items-center justify-center p-8">
              <div className="w-full max-w-md space-y-6">
                <div className="text-center">
                  <Lock className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <h2 className="text-2xl font-bold text-gray-900">
                    Kiosk settings
                  </h2>
                  <p className="mt-2 text-sm text-gray-600">
                    Enter password to access kiosk settings
                  </p>
                </div>

                <form onSubmit={handlePasswordSubmit} className="space-y-4">
                  <div>
                    <label
                      htmlFor="password"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Password
                    </label>
                    <input
                      id="password"
                      type="password"
                      value={passwordInput}
                      onChange={(e) => setPasswordInput(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter password"
                      autoFocus
                    />
                    {showPasswordError && (
                      <p className="mt-2 text-sm text-red-600">
                        Incorrect password. Please try again.
                      </p>
                    )}
                  </div>

                  <button
                    type="submit"
                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Unlock Settings
                  </button>
                </form>
              </div>
            </div>
          ) : (
            <>
              {/* Tab bar + Content Layout */}
              <div className="flex-1 flex flex-col overflow-hidden">
                {/* Horizontal scrollable tab bar */}
                <nav className="flex overflow-x-auto border-b border-gray-200 shrink-0">
                  {(
                    [
                      { id: "inspect", icon: Eye, label: "Inspect" },
                      { id: "errors", icon: X, label: "Errors" },
                      { id: "health", icon: Stethoscope, label: "Health" },
                      { id: "jump", icon: ChevronRight, label: "Jump" },
                      { id: "settings", icon: Settings, label: "Settings" },
                      { id: "stripe", icon: CreditCard, label: "Stripe" },
                      { id: "video", icon: Video, label: "Video" },
                      { id: "trace", icon: Activity, label: "Trace" },
                      { id: "network", icon: Wifi, label: "Network" },
                      { id: "storage", icon: HardDrive, label: "Storage" },
                      { id: "patchNotes", icon: FileText, label: "Patches" },
                    ] as const
                  ).map(({ id, icon: Icon, label }) => (
                    <button
                      key={id}
                      type="button"
                      onClick={() => setActiveTab(id)}
                      className={`shrink-0 px-3 py-2 text-xs font-medium transition-colors flex items-center gap-1.5 border-b-2 ${
                        activeTab === id
                          ? "border-blue-600 text-blue-700"
                          : "border-transparent text-gray-600 hover:text-gray-900"
                      }`}
                    >
                      <Icon className="h-3.5 w-3.5" />
                      <span>{label}</span>
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="shrink-0 px-3 py-2 text-xs font-medium text-gray-400 hover:text-gray-600 transition-colors flex items-center gap-1.5 ml-auto border-b-2 border-transparent"
                    title="Logout from DevTools"
                  >
                    <Lock className="h-3.5 w-3.5" />
                    <span>Logout</span>
                  </button>
                </nav>

                {/* Main Content Area */}
                <div className="flex-1 overflow-auto">
                  {activeTab === "inspect" && (
                    <InspectTab
                      state={state}
                      sizeInfo={sizeInfo}
                      onCopy={handleCopy}
                      copied={copied}
                      onContextChange={onContextChange}
                      onStateChange={handleJumpToState}
                    />
                  )}
                  {activeTab === "errors" && (
                    <ErrorsTab context={state.context} />
                  )}
                  {activeTab === "health" && (
                    <HealthCheckTab
                      configHealthCheck={configHealthCheck}
                      onRerunHealthCheck={onRerunHealthCheck}
                    />
                  )}
                  {activeTab === "jump" && (
                    <JumpTab
                      states={flow.states}
                      currentState={currentStateName}
                      onJump={handleJumpToState}
                      machine={machine}
                    />
                  )}
                  {activeTab === "settings" && (
                    <SettingsTab
                      context={state.context}
                      onContextChange={onContextChange}
                      inactivityTimeout={inactivityTimeout}
                      onInactivityTimeoutChange={onInactivityTimeoutChange}
                      stripeReaderId={stripeReaderId}
                      onStripeReaderIdChange={onStripeReaderIdChange}
                    />
                  )}
                  {activeTab === "stripe" && (
                    <StripeTab
                      stripeReaderId={stripeReaderId}
                      onStripeReaderIdChange={onStripeReaderIdChange}
                      orgId={state.context.orgId as number | undefined}
                      practiceId={
                        Number.parseInt(state.context.practiceId as string) as
                          | number
                          | undefined
                      }
                    />
                  )}
                  {activeTab === "video" && <VideoTab />}
                  {activeTab === "trace" && <TraceTab />}
                  {activeTab === "network" && <NetworkTab />}
                  {activeTab === "storage" && <StorageTab />}
                  {activeTab === "patchNotes" && <PatchNotesTab />}
                </div>
              </div>
            </>
          )}
        </KioskDialogContent>
      </KioskDialog>
    </div>
  );
}
